import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, AlertTriangle } from 'lucide-react';
import { useApi } from '../lib/api';
import { dateTimeCentral, periodLabel } from '../lib/format';
import PeriodPicker, { usePeriod } from '../components/PeriodPicker';

interface Status {
  period: string;
  isQuarterEnd: boolean;
  hubClosed: boolean | { error: string };
  rowcalUpload: { upload_id: number; uploaded_at: string; filename: string } | null;
  unmappedAccounts: number;
  ttmRowcalMissing: string[];
}

function Step({ ok, warn, title, children, to }: { ok: boolean; warn?: boolean; title: string; children: React.ReactNode; to: string }) {
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : Circle;
  return (
    <Link to={to} className="card p-4 flex gap-3 hover:border-denim transition-colors">
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${ok ? 'text-positive' : warn ? 'text-caution' : 'text-allstar-gray'}`} />
      <div>
        <div className="font-heading font-semibold text-denim">{title}</div>
        <div className="text-sm text-allstar-gray-dark">{children}</div>
      </div>
    </Link>
  );
}

export default function Overview() {
  const api = useApi();
  const [period, setPeriod] = usePeriod();
  const status = useQuery({
    queryKey: ['status', period],
    queryFn: () => api.get<Status>(`/api/outputs/status?period=${period}`),
  });
  const s = status.data;
  const hubOk = s?.hubClosed === true;
  const qs = `?period=${period}`;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl text-denim">Lender Reporting — {periodLabel(period)}</h1>
          <p className="text-sm text-allstar-gray-dark mt-1">
            Rowcal intake → GL mapping → shared addbacks → internal view and MidCap package.
          </p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {status.error && <div className="text-negative text-sm">{(status.error as Error).message}</div>}

      {s && (
        <div className="space-y-3">
          <Step ok={hubOk} warn={!hubOk} title="Allstar period closed in the Reporting Hub" to={`/overview${qs}`}>
            {hubOk ? 'Closed — Allstar numbers come from the Hub cache, tied to Sage by account.'
              : typeof s.hubClosed === 'object' ? `Couldn't reach the Hub: ${s.hubClosed.error}` : 'Not closed yet — close the month in the Hub first.'}
          </Step>
          <Step ok={!!s.rowcalUpload} title="1 · Rowcal trial balance accepted" to={`/upload${qs}`}>
            {s.rowcalUpload ? `${s.rowcalUpload.filename} · ${dateTimeCentral(s.rowcalUpload.uploaded_at)}` : 'No accepted upload for this month.'}
          </Step>
          <Step ok={!!s.rowcalUpload && s.unmappedAccounts === 0} warn={s.unmappedAccounts > 0} title="2 · Every Rowcal P&L account mapped" to={`/mapping${qs}`}>
            {s.unmappedAccounts ? `${s.unmappedAccounts} account(s) need a mapping.` : 'All mapped.'}
          </Step>
          <Step ok={hubOk && !!s.rowcalUpload} title="3 · Review addbacks & synergies" to={`/adjustments${qs}`}>
            Manual inputs (synergies, Vault launch) and the shared EBITDA ladder.
          </Step>
          <Step ok={false} warn={s.isQuarterEnd && s.ttmRowcalMissing.length > 0} title="4 · Generate outputs" to={`/outputs${qs}`}>
            Internal view and MidCap monthly{s.isQuarterEnd ? ' + quarterly covenant workbook' : ''}.
            {s.isQuarterEnd && s.ttmRowcalMissing.length > 0 &&
              ` Quarterly needs 12 months of Rowcal uploads — missing ${s.ttmRowcalMissing.join(', ')}.`}
          </Step>
        </div>
      )}
    </div>
  );
}
